import type { ServerAnalysis } from "@ayla/conversation";

/** PACE backend response shape (response.data) */
export type PaceAnalysisData = {
  agent_rating?: number;
  clarity_of_explanation?: number;
  empathy_shown?: number;
  objection_handling?: number;
  enrollment_success?: boolean;
  key_strengths?: string;
  areas_for_improvement?: string;
  [key: string]: unknown;
};

/** Raw PACE API response (wraps data) */
export type PaceApiResponse = {
  data?: PaceAnalysisData;
  [key: string]: unknown;
};

function splitIntoLines(s: string | undefined): string[] {
  if (!s || typeof s !== "string") return [];
  return s
    .split(/[\n•]/)
    .map(t => t.trim())
    .filter(Boolean);
}

/**
 * Maps PACE backend response to ServerAnalysis shape expected by FeedbackCard.
 * Converts 1–5 scale scores to percentages where needed.
 */
export function mapPaceResponseToServerAnalysis(
  raw: PaceApiResponse
): ServerAnalysis {
  const d = raw.data ?? {};
  const rawRating = Number(d?.agent_rating);
  const agentRating = Math.min(
    5,
    Math.max(0, Number.isNaN(rawRating) ? 3 : rawRating)
  );
  const clinicianPct = Math.round(agentRating * 20);
  const patientPct = 100 - clinicianPct;

  const strengths = splitIntoLines(d?.key_strengths);
  const opportunity = (d?.areas_for_improvement ?? "").trim() || undefined;

  return {
    talkTime: {
      clinicianPct,
      patientPct,
    },
    questions: {
      open: 0,
      closed: 0,
      openPct: 0,
    },
    coach: {
      strengths: strengths.length ? strengths : undefined,
      opportunity,
      empathy_examples: undefined,
      reflective_prompt: undefined,
    },
    empathy: {
      count: typeof d?.empathy_shown === "number" ? d.empathy_shown : undefined,
      indicators: undefined,
      moments: undefined,
    },
  };
}

/** Returns true if the response is the PACE shape (has data.agent_rating etc.) */
export function isPaceResponse(raw: unknown): raw is PaceApiResponse {
  if (!raw || typeof raw !== "object") return false;
  const obj = raw as Record<string, unknown>;
  const data = obj.data as Record<string, unknown> | undefined;
  return (
    data != null &&
    typeof data === "object" &&
    ("agent_rating" in data ||
      "enrollment_success" in data ||
      "key_strengths" in data)
  );
}
